/**
 * ⚠️  INTERNAL USE ONLY — NOT FOR PUBLIC ACCESS
 * Route: /admin/products
 * Password-gated. noindex. Not linked from any public page.
 *
 * Manages upsell products and add-on offerings:
 *   — Accessories (HDMI cables, screen protectors, chargers, docks)
 *   — Add-on services (cleaning, JoyCon TMR upgrades, annual plans)
 *   — Custom controller builds
 */
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Search, Pencil, Trash2, X, Check,
  Download, RefreshCw, Package, Home, Zap, Settings, ExternalLink,
  Gamepad2, ChevronDown, ChevronUp, Shield,
} from '../../lib/icons';
import { AdminLoginGate, isAdminAuthed } from '../../components/AdminLoginGate';
import type { ServiceEntry, ServiceStatus, DeviceCategory } from '../../types/services';
import {
  getProducts, updateProduct, addProduct, deleteProduct,
  resetProductsToInitial, exportProductsJSON,
} from '../../lib/products-store';
import { DEVICE_CATEGORY_LABELS } from '../../types/services';
import SEOHead from '../../components/SEOHead';


// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (p: number | null) =>
  p === null
    ? <span className="text-yellow-400 text-xs font-semibold">TBD</span>
    : <span>${p}</span>;

const STATUS_COLORS: Record<ServiceStatus, string> = {
  active: 'bg-green-500/20 text-green-400 border-green-500/40',
  draft:  'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
  hidden: 'bg-gray-500/20 text-gray-400 border-gray-500/40',
};

const CAT_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all',            label: 'All Products' },
  { value: 'ps5',            label: 'PS5' },
  { value: 'xbox',           label: 'Xbox' },
  { value: 'switch',         label: 'Nintendo Switch' },
  { value: 'controller',     label: 'Controllers' },
  { value: 'laptop-macbook', label: 'MacBook & Laptops' },
  { value: 'handheld-pc',    label: 'Handheld PC' },
];

const BLANK_PRODUCT: Omit<ServiceEntry, 'id' | 'createdAt' | 'updatedAt'> = {
  sku: '', status: 'active', deviceCategory: 'ps5', deviceModels: [],
  repairCategory: 'repair', repairType: '', description: '',
  basePrice: null, rushPrice: null, priceTiers: [],
  turnaround: '', rushTurnaround: '', warranty: '',
  mailInAvailable: true, internalNotes: '', disclaimer: '', tags: [],
  imageUrl: '', imageAlt: '', whatToBring: '',
  upsellIds: [], isUpsell: true,
};


// ── Product form modal ────────────────────────────────────────────────────────
const ProductModal = ({
  initial,
  onSave,
  onClose,
}: {
  initial: ServiceEntry | null;
  onSave: (p: ServiceEntry | Omit<ServiceEntry, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onClose: () => void;
}) => {
  const isNew = initial === null;
  const [form, setForm] = useState<Omit<ServiceEntry, 'id' | 'createdAt' | 'updatedAt'>>(
    initial ? { ...initial } : { ...BLANK_PRODUCT }
  );

  const set = <K extends keyof typeof form>(k: K, v: typeof form[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.repairType.trim()) return;
    if (isNew) {
      onSave(form);
    } else {
      onSave({ ...initial!, ...form });
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-2xl my-8 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <h2 className="text-[#E0E0E0] font-bold text-lg">
            {isNew ? 'Add Product' : 'Edit Product'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-[#E0E0E0] transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">Product Name *</label>
            <input
              value={form.repairType}
              onChange={e => set('repairType', e.target.value)}
              placeholder="e.g. Magnetic HDMI Breakaway Adapter"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14] transition-colors"
              required
            />
          </div>

          {/* Category + Status row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">Category</label>
              <select
                value={form.deviceCategory}
                onChange={e => set('deviceCategory', e.target.value as DeviceCategory)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14] transition-colors"
              >
                {Object.entries(DEVICE_CATEGORY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">Status</label>
              <select
                value={form.status}
                onChange={e => set('status', e.target.value as ServiceStatus)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14] transition-colors"
              >
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="hidden">Hidden</option>
              </select>
            </div>
          </div>

          {/* Price */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">Price (leave blank for TBD / Quote)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.basePrice ?? ''}
                onChange={e => set('basePrice', e.target.value ? parseFloat(e.target.value) : null)}
                placeholder="e.g. 20"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-7 pr-3 py-2.5 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14] transition-colors"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">Description (customer-facing)</label>
            <textarea
              value={form.description}
              onChange={e => set('description', e.target.value)}
              rows={3}
              placeholder="Briefly describe what this product is and why it's useful..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14] transition-colors resize-none"
            />
          </div>

          {/* Disclaimer */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">Disclaimer / Note (optional)</label>
            <input
              value={form.disclaimer}
              onChange={e => set('disclaimer', e.target.value)}
              placeholder="e.g. Requires prior cleaning service"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14] transition-colors"
            />
          </div>

          {/* Internal notes */}
          <div>
            <label className="block text-xs text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">Internal Notes (not shown to customers)</label>
            <input
              value={form.internalNotes}
              onChange={e => set('internalNotes', e.target.value)}
              placeholder="Notes for your team only"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-[#E0E0E0] text-sm focus:outline-none focus:border-[#39FF14] transition-colors"
            />
          </div>

          {/* isUpsell toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => set('isUpsell', !form.isUpsell)}
              className={`w-10 h-6 rounded-full transition-colors ${form.isUpsell ? 'bg-[#39FF14]' : 'bg-gray-700'}`}
            >
              <span className={`block w-4 h-4 rounded-full bg-white shadow transition-transform mx-1 ${form.isUpsell ? 'translate-x-4' : 'translate-x-0'}`} />
            </button>
            <div>
              <p className="text-sm text-[#E0E0E0] font-medium">Show as upsell add-on</p>
              <p className="text-xs text-gray-500">When enabled this product can appear in the booking add-ons step</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-800">
            <button type="button" onClick={onClose}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] rounded-lg text-sm transition-colors">
              Cancel
            </button>
            <button type="submit"
              className="px-5 py-2 bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold rounded-lg text-sm transition-colors flex items-center gap-1.5">
              <Check className="h-4 w-4" />
              {isNew ? 'Add Product' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Delete confirm dialog ─────────────────────────────────────────────────────
const DeleteConfirm = ({ name, onConfirm, onCancel }: { name: string; onConfirm: () => void; onCancel: () => void }) => (
  <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
    <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-sm p-6 shadow-2xl">
      <h3 className="text-[#E0E0E0] font-bold text-lg mb-2">Delete Product?</h3>
      <p className="text-[#B0B0B0] text-sm mb-5">
        <span className="text-[#E0E0E0] font-semibold">"{name}"</span> will be permanently removed.
        This cannot be undone.
      </p>
      <div className="flex gap-3">
        <button onClick={onCancel}
          className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] rounded-lg text-sm transition-colors">
          Cancel
        </button>
        <button onClick={onConfirm}
          className="flex-1 px-4 py-2 bg-[#FF1744] hover:bg-red-500 text-white font-bold rounded-lg text-sm transition-colors">
          Delete
        </button>
      </div>
    </div>
  </div>
);

// ── Product row ───────────────────────────────────────────────────────────────
const ProductRow = ({
  product,
  onEdit,
  onDelete,
}: {
  product: ServiceEntry;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = !!(product.description || product.disclaimer || product.internalNotes);

  return (
    <div className="border-b border-gray-800/60 last:border-b-0">
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-gray-900/40 transition-colors">
        {/* Expand toggle */}
        <button
          onClick={() => hasDetails && setExpanded(e => !e)}
          className={`shrink-0 ${hasDetails ? 'cursor-pointer text-gray-500 hover:text-gray-300' : 'text-gray-800 cursor-default'}`}
        >
          {expanded
            ? <ChevronUp className="h-3.5 w-3.5" />
            : <ChevronDown className="h-3.5 w-3.5" />}
        </button>

        {/* Name + category */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-[#E0E0E0] truncate">{product.repairType}</span>
            {product.isUpsell && (
              <span className="text-[10px] font-bold uppercase tracking-wide bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/30 px-1.5 py-0.5 rounded shrink-0">
                Upsell
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {DEVICE_CATEGORY_LABELS[product.deviceCategory]}
          </p>
        </div>

        {/* Price */}
        <span className="text-sm font-bold tabular-nums text-[#E0E0E0] shrink-0">
          {fmt(product.basePrice)}
        </span>

        {/* Status badge */}
        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border shrink-0 ${STATUS_COLORS[product.status]}`}>
          {product.status}
        </span>

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={onEdit}
            className="h-7 w-7 flex items-center justify-center rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-[#E0E0E0] transition-colors">
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button onClick={onDelete}
            className="h-7 w-7 flex items-center justify-center rounded-lg bg-gray-800 hover:bg-[#FF1744]/20 text-gray-400 hover:text-[#FF1744] transition-colors">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && hasDetails && (
        <div className="px-10 pb-3 space-y-1.5 bg-gray-950/40 border-t border-gray-800/40">
          {product.description && (
            <p className="text-xs text-gray-400 pt-2 leading-relaxed">{product.description}</p>
          )}
          {product.disclaimer && (
            <p className="text-xs text-yellow-400/80 italic">⚠ {product.disclaimer}</p>
          )}
          {product.internalNotes && (
            <p className="text-xs text-gray-600 italic">Internal: {product.internalNotes}</p>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminProductsPage() {
  const [authed, setAuthed]   = useState(isAdminAuthed);
  const [products, setProducts] = useState<ServiceEntry[]>(() => isAdminAuthed() ? getProducts() : []);

  const [searchQ, setSearchQ] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [editTarget, setEditTarget] = useState<ServiceEntry | 'new' | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ServiceEntry | null>(null);
  const [toast, setToast]   = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const handleAuth = () => {
    setAuthed(true);
    setProducts(getProducts());
  };

  // ── Filtered list ──────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = products;
    if (catFilter !== 'all') list = list.filter(p => p.deviceCategory === catFilter);
    if (searchQ.trim()) {
      const q = searchQ.toLowerCase();
      list = list.filter(p =>
        p.repairType.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.tags.some(t => t.toLowerCase().includes(q))
      );
    }
    return list;
  }, [products, catFilter, searchQ]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:    products.length,
    active:   products.filter(p => p.status === 'active').length,
    upsells:  products.filter(p => p.isUpsell).length,
    tbd:      products.filter(p => p.basePrice === null).length,
  }), [products]);

  // ── CRUD ───────────────────────────────────────────────────────────────────
  const handleSave = (data: ServiceEntry | Omit<ServiceEntry, 'id' | 'createdAt' | 'updatedAt'>) => {
    if ('id' in data) {
      setProducts(updateProduct(data as ServiceEntry));
      showToast('✓ Product updated');
    } else {
      setProducts(addProduct(data));
      showToast('✓ Product added');
    }
    setEditTarget(null);
  };

  const handleDelete = (product: ServiceEntry) => {
    setDeleteTarget(product);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setProducts(deleteProduct(deleteTarget.id));
    showToast('Product deleted');
    setDeleteTarget(null);
  };

  const handleReset = () => {
    if (!confirm('Reset all products to defaults? This cannot be undone.')) return;
    setProducts(resetProductsToInitial());
    showToast('✓ Reset to defaults');
  };

  const handleExport = () => {
    const blob = new Blob([exportProductsJSON()], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `gfs-products-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) return <AdminLoginGate onAuth={handleAuth} />;

  return (
    <>
      <SEOHead
        title="Product Management — GameFix Solutions Admin"
        description="Internal product management — not for public access."
        noIndex
      />

      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-gray-800 border border-[#39FF14]/40 text-[#39FF14] px-4 py-2 rounded-lg text-sm font-semibold shadow-xl">
          {toast}
        </div>
      )}

      {/* Edit modal */}
      {editTarget !== null && (
        <ProductModal
          initial={editTarget === 'new' ? null : editTarget}
          onSave={handleSave}
          onClose={() => setEditTarget(null)}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <DeleteConfirm
          name={deleteTarget.repairType}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0]">
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <header className="bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Shield className="h-5 w-5 text-[#FF1744] shrink-0" />
            <div>
              <h1 className="font-bold text-[#E0E0E0]">Product Management</h1>
              <p className="text-xs text-[#FF1744] font-semibold tracking-wide">INTERNAL USE ONLY — GAMEFIX SOLUTIONS</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Nav links */}
            <Link to="/admin/services"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-[#E0E0E0] transition-colors">
              <Settings className="h-3.5 w-3.5" /> Services
            </Link>
            <Link to="/admin/phone-repair-preview"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A1628] hover:bg-[#0C1F3A] border border-[#1E3A5F] rounded-lg text-sm text-[#0EA5E9] transition-colors">
              📱 Phone Repair
            </Link>
            <Link to="/custom-controllers"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-[#E0E0E0] transition-colors"
              target="_blank">
              <Gamepad2 className="h-3.5 w-3.5 text-[#39FF14]" /> Custom Controllers <ExternalLink className="h-3 w-3 text-gray-600" />
            </Link>
            <Link to="/book-repair"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-[#E0E0E0] transition-colors">
              <Zap className="h-3.5 w-3.5 text-[#39FF14]" /> Book Repair
            </Link>
            <Link to="/"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-[#E0E0E0] transition-colors">
              <Home className="h-3.5 w-3.5" /> Main Site
            </Link>
            <button onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-[#E0E0E0] transition-colors">
              <Download className="h-3.5 w-3.5" /> Export
            </button>
            <button onClick={handleReset}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-gray-400 transition-colors"
              title="Reset to built-in defaults">
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setEditTarget('new')}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-[#39FF14] hover:bg-[#00BFFF] text-[#0D0D0D] font-bold rounded-lg text-sm transition-colors">
              <Plus className="h-4 w-4" /> Add Product
            </button>
          </div>
        </header>

        {/* ── Info bar ────────────────────────────────────────────────────── */}
        <div className="px-6 py-2 border-b border-gray-800 bg-gray-900/40 flex items-center gap-2 text-xs text-gray-500">
          <Package className="h-3.5 w-3.5 shrink-0" />
          Products shown here are merged into the booking form as add-ons.
          Upsell links between services and products are defined in&nbsp;
          <code className="text-gray-400 bg-gray-800 px-1 rounded">src/data/upsell-products.ts</code>.
        </div>

        {/* ── Stats bar ───────────────────────────────────────────────────── */}
        <div className="grid grid-cols-4 divide-x divide-gray-800 border-b border-gray-800">
          {[
            { label: 'Total',     value: stats.total,   color: 'text-[#E0E0E0]' },
            { label: 'Active',    value: stats.active,  color: 'text-green-400' },
            { label: 'Upsells',   value: stats.upsells, color: 'text-[#39FF14]' },
            { label: 'TBD Price', value: stats.tbd,     color: 'text-yellow-400' },
          ].map(s => (
            <div key={s.label} className="px-6 py-3 bg-gray-900/30">
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* ── Filters ─────────────────────────────────────────────────────── */}
        <div className="px-6 py-3 border-b border-gray-800 flex items-center gap-3 flex-wrap bg-gray-900/20">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
            <input
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Search products…"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-[#E0E0E0] placeholder-gray-600 focus:outline-none focus:border-[#39FF14] transition-colors"
            />
            {searchQ && (
              <button onClick={() => setSearchQ('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Category tabs */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {CAT_FILTER_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setCatFilter(opt.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  catFilter === opt.value
                    ? 'bg-[#39FF14] text-[#0D0D0D]'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-[#E0E0E0]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Product list ─────────────────────────────────────────────────── */}
        <main className="max-w-5xl mx-auto px-4 py-6">
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <Package className="h-10 w-10 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No products match your filters</p>
              {(searchQ || catFilter !== 'all') && (
                <button
                  onClick={() => { setSearchQ(''); setCatFilter('all'); }}
                  className="mt-3 text-xs text-[#39FF14] hover:underline">
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl overflow-hidden">
              {/* Column headers */}
              <div className="grid grid-cols-[1.5rem_1fr_auto_auto_5rem] gap-3 px-4 py-2 border-b border-gray-800 text-[10px] font-bold uppercase tracking-wider text-gray-600">
                <span />
                <span>Product</span>
                <span className="text-right">Price</span>
                <span>Status</span>
                <span className="text-right">Actions</span>
              </div>

              {filtered.map(product => (
                <ProductRow
                  key={product.id}
                  product={product}
                  onEdit={() => setEditTarget(product)}
                  onDelete={() => handleDelete(product)}
                />
              ))}
            </div>
          )}

          {/* Custom controllers callout */}
          <div className="mt-6 bg-gray-900/40 border border-[#39FF14]/20 rounded-2xl p-5 flex items-start gap-4">
            <Gamepad2 className="h-8 w-8 text-[#39FF14] shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-[#E0E0E0] font-bold text-sm mb-1">Custom PS5 Controllers</h3>
              <p className="text-gray-400 text-xs leading-relaxed mb-3">
                Custom controller builds (shell swaps from $150, graphic builds from $190) are managed
                on the Custom Controllers page — not stored here. The upsell entry above
                (<span className="text-[#E0E0E0] font-medium">Custom PS5 Controller Build</span>) links
                customers to that page from the booking form.
              </p>
              <div className="flex gap-2 flex-wrap">
                <Link to="/custom-controllers" target="_blank"
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#39FF14]/10 hover:bg-[#39FF14]/20 border border-[#39FF14]/30 text-[#39FF14] rounded-lg text-xs font-semibold transition-colors">
                  <ExternalLink className="h-3 w-3" /> View Custom Controllers Page
                </Link>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
